import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../config/URL";
import { toast } from "react-toastify";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";
import fetchAllTeacherListByCenter from "../List/TeacherListByCenter";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";
import fetchAllCentersWithIds from "../List/CenterList";
import Select from "react-select";
import fetchAllCoursesWithIds from "../List/CourseList";
import fetchAllTeachersWithIds from "../List/TeacherList";
import fetchAllStudentsWithIds from "../List/StudentList";
import { MultiSelect } from "react-multi-select-component";
import CustomDatePicker from "../CustomDatePicker";

const validationSchema = Yup.object({
  center: Yup.string().required("*Centre is required"),
  course: Yup.string().required("*Course is required"),
  userId: Yup.string().required("*Teacher is required"),
  day: Yup.string().required("*Days is required"),
  batchTime: Yup.string().required("*Batch Time is required"),
  classListing: Yup.string().required("*Class Listing is required"),
  folderCategoryListing: Yup.string().required("*FolderCategory is required"),
  date: Yup.string().required("*Date is required"),
  expiredDate: Yup.string().required("*Expired Date is required"),
  studentIds: Yup.array()
    .min(1, "*At least one student must be selected")
    .required("*Student selection is required"),
});

function DocumentAdd({ selectedCenter }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [folderCategory, setFolderCategory] = useState("individual");
  const [centerData, setCenterData] = useState(null);
  const [classData, setClassData] = useState(null);
  const [classCenter, setClassCenter] = useState(null);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const userName = localStorage.getItem("userName");
  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("userName");
  const [batchData, setBatchData] = useState(null);
  const [courseOptions, setCourseOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [startEndDate, setStartEndDate] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  const formatDisplayDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d)) return "";

    return d.toLocaleDateString("en-US", {
      month: "short",
      weekday: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formik = useFormik({
    initialValues: {
      center: selectedCenter,
      course: "",
      userId: "" || userId,
      classId: "",
      classListing: "",
      date: "",
      day: "",
      expiredDate: "",
      folderCategoryListing: "individual",
      batchTime: "",
      groupSelect: "",
      studentSelect: "",
      folderName: "",
      studentIds: [],
      createdBy: userName,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);

      try {
        let selectedOptionName = "";
        let selectedClassName = "";
        let selectedCourseName = "";
        let selectedTeacherName = "";
        let selectedStudentName = "";

        console.log("Selected Center Object:", values.center);

        let classCenterData = classCenter;

        if (!selectedCenter || selectedCenter === "0") {
          try {
            const response = await api.get(
              `/getAllCourseClassListingsById/${values.classListing}`,
            );
            classCenterData = response.data;
            setClassCenter(response.data);
          } catch (error) {
            toast.error("Error Fetching Data");
            setLoadIndicator(false);
            return;
          }
        }

        selectedOptionName = classCenterData?.centerName || "--";

        selectedClassName =
          classOptions.find(
            (cls) => parseInt(values.classListing) === cls.value,
          )?.label || "--";

        selectedCourseName =
          courseOptions.find(
            (course) => parseInt(values.course) === course.value,
          )?.label || "--";

        selectedTeacherName =
          teacherOptions.find((user) => parseInt(values.userId) === user.value)
            ?.label || "--";

        selectedStudentName =
          studentOptions.find(
            (student) => parseInt(values.studentIds) === student.value,
          )?.label || "--";

        let requestBody = {
          centerId: classCenterData?.centerId || selectedCenter,
          userId: values.userId,
          user: selectedTeacherName,
          day: values.day,
          center: selectedOptionName,
          classListing: selectedClassName,
          course: selectedCourseName,
          courseId: values.course,
          classId: values.classListing,
          folderCategory: folderCategory,
          batchTime: values.batchTime,
          date: values.date,
          expiredDate: values.expiredDate,
          createdBy: userName,
          isGroupUpload: folderCategory === "group",
        };

        if (folderCategory === "group") {
          requestBody.isGroupUpload = true;
        } else {
          requestBody.isGroupUpload = false;
          requestBody.studentIds = values.studentIds;
          requestBody.studentName = selectedStudentName;
          if (values.studentIds.length === 1 && values.folderName) {
            requestBody.folderName = values.folderName;
          }
        }

        const response = await api.post(
          "/uploadStudentFilesWithSingleOrGroup",
          requestBody,
        );

        if (response.status === 201) {
          toast.success(response.data.message);
          navigate("/document");
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (
          error?.response?.status === 409 ||
          error?.response?.status === 207 ||
          error?.response?.status === 404
        ) {
          toast.warning(error?.response?.data?.message);
        } else {
          toast.error("Error submitting data:", error);
        }
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  const scrollToError = (errors) => {
    const errorField = Object.keys(errors)[0];
    const errorElement = document.querySelector(`[name="${errorField}"]`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      errorElement.focus();
    }
  };

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      scrollToError(formik.errors);
    }
  }, [formik.submitCount, formik.errors]);

  // === AUTO-FILL FROM URL PARAMS ===
  useEffect(() => {
    const studentId = searchParams.get("studentId");
    const courseId = searchParams.get("courseId");
    const classId = searchParams.get("classId");
    const batch = searchParams.get("batch");
    const day = searchParams.get("day");
    const userIdParam = searchParams.get("userId");

    if (studentId || courseId || classId || batch || day || userIdParam) {
      const autoFill = async () => {
        try {
          // Step 1: Get Class Details First (most important)
          if (classId) {
            const classRes = await api.get(
              `/getAllCourseClassListingsById/${classId}`,
            );
            const cls = classRes.data;

            const centerId = cls.centerId || selectedCenter || "0";

            // Set Center
            formik.setFieldValue("center", centerId.toString());

            // Fetch dependent data
            await Promise.all([fetchCourses(centerId), fetchTeacher(centerId)]);

            // Set Course & Class
            formik.setFieldValue("course", cls.courseId.toString());
            formik.setFieldValue("classListing", cls.id.toString());

            // Set Teacher
            if (userIdParam) {
              formik.setFieldValue("userId", userIdParam);
            }

            // Set Day & Batch Time
            if (day) formik.setFieldValue("day", day.toUpperCase());
            if (batch) formik.setFieldValue("batchTime", batch);

            // Set Date (use startDate if available)
            if (cls.startDate) {
              formik.setFieldValue("date", cls.startDate);
            }

            // Set Student
            if (studentId) {
              formik.setFieldValue("studentIds", [parseInt(studentId)]);
            }

            setClassData(cls);
            setClassCenter(cls);
          }
        } catch (err) {
          console.error("Auto-fill failed:", err);
          toast.error("Failed to auto-fill form");
        }
      };

      autoFill();
    }
  }, [searchParams, selectedCenter]);

  const fetchData = async () => {
    try {
      const response = await fetchAllCentersWithIds();
      if (Array.isArray(response)) {
        setCenterData(response);
        formik.setFieldValue("center", selectedCenter);
        fetchCourses(selectedCenter);
        fetchTeacher(selectedCenter);
      } else {
        console.error("Invalid data format:", response);
        setCenterData([]);
      }
    } catch (error) {
      toast.error("Failed to fetch center data");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
    if (selectedCenter) {
      formik.setFieldValue("center", selectedCenter);
    }
  }, [selectedCenter]);

  const fetchCourses = async (centerId) => {
    try {
      let courses = [];
      if (userId && userName === "SMS_TEACHER") {
        const response = await api.get(`/getCoursesByUserId/${userId}`);
        courses = response.data;
      } else {
        const numericCenterId = Number(centerId);
        if (numericCenterId === 0) {
          courses = await fetchAllCoursesWithIds();
        } else {
          courses = await fetchAllCoursesWithIdsC(numericCenterId);
        }
      }
      if (!Array.isArray(courses)) {
        throw new Error("API did not return an array");
      }
      const formattedCourses = courses.map((course) => ({
        value: course.id,
        label: course.courseNames || course.courseName,
      }));
      setCourseOptions(formattedCourses);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error(error.message || "Failed to fetch courses");
    }
  };

  const fetchTeacher = async (centerId) => {
    try {
      let teacherList = [];
      const numericCenterId = Number(centerId);
      if (numericCenterId === 0) {
        teacherList = await fetchAllTeachersWithIds();
      } else {
        teacherList = await fetchAllTeacherListByCenter(numericCenterId);
      }
      if (!Array.isArray(teacherList)) {
        throw new Error("API did not return an array");
      }
      const formattedCourses = teacherList.map((teacher) => ({
        value: teacher.id,
        label: teacher.teacherNames,
      }));
      setTeacherOptions(formattedCourses);
    } catch (error) {
      console.error("Error fetching Teacher:", error);
      toast.error(error.message || "Failed to fetch courses");
    }
  };

  const fetchStudentsForDocument = async ({
    courseId,
    classId,
    userId,
    day,
    batchTime,
  }) => {
    // ⛔ Do NOT call API unless ALL values are present
    if (!courseId || !classId || !userId || !day || !batchTime) {
      setStudentOptions([]); // Clear dropdown
      return;
    }

    try {
      const response = await api.get(
        `/getStudentsForDocument?courseId=${courseId}&classId=${classId}&userId=${userId}&days=${day}&batch=${encodeURIComponent(
          batchTime,
        )}`,
      );

      const students = response.data;

      if (!Array.isArray(students)) {
        throw new Error("Invalid student data");
      }

      const formattedStudents = students.map((s) => ({
        value: s.id,
        label: s.studentNames,
      }));

      setStudentOptions(formattedStudents);
    } catch (error) {
      console.error("Student fetch error:", error);
      toast.error("Failed to load students");
      setStudentOptions([]);
    }
  };

  const fetchClasses = async (courseId) => {
    try {
      if (!courseId) return;

      let response;

      if (role === "SMS_ADMIN") {
        response = await api.get(`getClassIdsAndNamesByCourseId/${courseId}`);
      } else {
        response = await api.get(
          `getClassIdsAndNamesByCourseId/${courseId}?userId=${userId}`,
        );
      }

      const formattedClasses = response.data.map((classListing) => ({
        value: classListing.id,
        label: classListing.classNames,
      }));

      setClassOptions(formattedClasses);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load classes");
    }
  };

  const fetchStartandEndDate = async (classId) => {
    try {
      if (classId) {
        const response = await api.get(
          `/getScheduledStartDates?classId=${classId}`,
        );
        const formattedDates = response.data.map((dateItem) => ({
          value: dateItem.date,
          label: dateItem.showDate || dateItem.date,
        }));
        setStartEndDate(formattedDates);
      } else {
        setStartEndDate([]);
      }
    } catch (error) {
      toast.error("Error fetching start and end dates: " + error.message);
      setStartEndDate([]);
    }
  };

  const fetchFolderName = async (
    centerId,
    courseId,
    classId,
    date,
    studentId,
  ) => {
    try {
      const response = await api.get(
        `/getFolderName?centerId=${centerId}&courseId=${courseId}&classId=${classId}&date=${date}&studentId=${studentId}`,
      );
      formik.setFieldValue("folderName", response.data.folderName);
    } catch (error) {
      console.error("Error fetching folder name:", error);
      toast.error("Failed to fetch folder name");
      formik.setFieldValue("folderName", "");
    }
  };

  const handleCourseChange = (selectedOption) => {
    const course = selectedOption ? selectedOption.value : "";

    setClassOptions([]); // ⬅️ important
    setClassData(null);
    setStartEndDate([]);
    setBatchData(null);

    formik.setValues({
      ...formik.values,
      course,
      classListing: null, // ✅ MUST be null
      day: "",
      batchTime: "",
      date: "",
      expiredDate: "",
      studentIds: [],
      folderName: "",
      userId: userName === "SMS_TEACHER" ? userId : "",
    });

    if (course) {
      fetchClasses(course);
    }
  };

  // useEffect(() => {
  //   if (formik.values.date) {
  //     const calculatedExpiredDate = calculateExpiryDate(formik.values.date);
  //     formik.setFieldValue("expiredDate", calculatedExpiredDate);
  //   }
  // }, [formik.values.date]);

  const calculateExpiryDate = (date) => {
    if (!date) return "";
    const selectedDate = new Date(date);
    if (isNaN(selectedDate.getTime())) return "";
    const expiryDate = new Date(
      selectedDate.getTime() + 30 * 24 * 60 * 60 * 1000,
    );
    const year = expiryDate.getFullYear();
    const month = String(expiryDate.getMonth() + 1).padStart(2, "0");
    const day = String(expiryDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleCenterChange = (event) => {
    const center = event.target.value || selectedCenter;
    setCourseOptions([]);
    setClassOptions([]);
    setTeacherOptions([]);
    setStudentOptions([]);
    setStartEndDate([]);
    formik.setFieldValue("center", center);
    formik.setFieldValue("folderName", "");
    fetchCourses(center);
    fetchTeacher(center);
  };

  const formatTo12Hour = (time) => {
    const [hours, minutes] = time.split(":");
    let period = "AM";
    let hour = parseInt(hours, 10);

    if (hour === 0) {
      hour = 12;
    } else if (hour >= 12) {
      period = "PM";
      if (hour > 12) hour -= 12;
    }

    return `${hour}:${minutes} ${period}`;
  };

  const normalizeTime = (time) => {
    if (time.includes("AM") || time.includes("PM")) {
      return time;
    }
    return formatTo12Hour(time);
  };

  const convertTo24Hour = (time) => {
    const [timePart, modifier] = time.split(" ");
    let [hours, minutes] = timePart.split(":").map(Number);

    if (modifier === "PM" && hours < 12) {
      hours += 12;
    } else if (modifier === "AM" && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}`;
  };

  const getClassData = async (classId) => {
    try {
      const response = await api.get(
        `/getAllCourseClassListingsById/${classId}`,
      );
      setClassData(response.data);
      setClassCenter(response.data);

      if (response.data) {
        formik.setFieldValue("day", response.data.day);
        formik.setFieldValue("batchTime", response.data.startTime);
        setBatchData([response.data.startTime]);
        formik.setFieldValue("date", response.data.startDate);
        formik.setFieldValue("expiredDate", response.data.endDate);
      }
    } catch (error) {
      toast.error("Error Fetching Class Data ", error);
    }
  };

  useEffect(() => {
    if (formik.values.classListing) {
      getClassData(formik.values.classListing);
      fetchStartandEndDate(formik.values.classListing);
    } else {
      formik.setFieldValue("day", "");
      formik.setFieldValue("batchTime", "");
      formik.setFieldValue("date", "");
      formik.setFieldValue("expiredDate", "");
      formik.setFieldValue("folderName", "");
      setBatchData(null);
      setStartEndDate([]);
    }
  }, [formik.values.classListing]);

  useEffect(() => {
    if (
      formik.values.center &&
      formik.values.course &&
      formik.values.classListing &&
      formik.values.date &&
      formik.values.studentIds.length === 1
    ) {
      fetchFolderName(
        formik.values.center,
        formik.values.course,
        formik.values.classListing,
        formik.values.date,
        formik.values.studentIds[0],
      );
    } else {
      formik.setFieldValue("folderName", "");
    }
  }, [
    formik.values.center,
    formik.values.course,
    formik.values.classListing,
    formik.values.date,
    formik.values.studentIds,
  ]);

  useEffect(() => {
    const { course, classListing, userId, day, batchTime } = formik.values;

    // 🚫 If ANY value missing → clear students & skip API
    if (!course || !classListing || !userId || !day || !batchTime) {
      setStudentOptions([]);
      formik.setFieldValue("studentIds", []);
      return;
    }

    fetchStudentsForDocument({
      courseId: course,
      classId: classListing,
      userId,
      day,
      batchTime,
    });
  }, [
    formik.values.course,
    formik.values.classListing,
    formik.values.userId,
    formik.values.day,
    formik.values.batchTime,
  ]);

  return (
    <div className="container">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Folder Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/document" className="custom-breadcrumb">
            Folder
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Folder Add
        </li>
      </ol>
      <form
        onSubmit={formik.handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !formik.isSubmitting) {
            e.preventDefault();
          }
        }}
      >
        <div className="card">
          <div
            className="d-flex justify-content-between align-items-center p-1 mb-4 px-4"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="dot active"></div>
              </div>
              <span className="me-2 text-muted">Add Folder</span>
            </div>
            <div className="my-2 pe-3 d-flex align-items-center">
              <Link to="/document">
                <button type="button " className="btn btn-sm btn-border">
                  Back
                </button>
              </Link>
                
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                )}
                <span className="fw-medium">Save</span>
              </button>
            </div>
          </div>

          <div className="container">
            <div className="row py-4">
              <div className="col-md-6 col-12 mb-4 d-none">
                <label className="">
                  Centre<span className="text-danger">*</span>
                </label>
                <select
                  {...formik.getFieldProps("center")}
                  name="center"
                  type="hidden"
                  className={`form-select ${
                    formik.touched.center && formik.errors.center
                      ? "is-invalid"
                      : ""
                  }`}
                  aria-label="Default select example"
                  onChange={handleCenterChange}
                >
                  <option disabled>Select a center</option>
                  {Array.isArray(centerData) &&
                    centerData.map((center) => (
                      <option key={center.id} value={center.id}>
                        {center.centerNames}
                      </option>
                    ))}
                </select>
                {formik.touched.center && formik.errors.center && (
                  <div className="invalid-feedback">{formik.errors.center}</div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label className="">
                  Course<span className="text-danger">*</span>
                </label>
                <Select
                  options={courseOptions}
                  name="course"
                  value={
                    courseOptions.find(
                      (option) => option.value === formik.values.course,
                    ) || null
                  }
                  onChange={handleCourseChange}
                  placeholder="Select Course"
                  isSearchable
                  isClearable
                  className={
                    formik.touched.course && formik.errors.course
                      ? "is-invalid"
                      : ""
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.course && formik.errors.course && (
                  <div className="invalid-feedback">{formik.errors.course}</div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label className="">
                  Class Listing<span className="text-danger">*</span>
                </label>
                <Select
                  options={classOptions}
                  name="classListing"
                  value={
                    formik.values.classListing
                      ? classOptions.find(
                          (option) =>
                            option.value === formik.values.classListing,
                        )
                      : null
                  }
                  onChange={(selectedOption) =>
                    formik.setFieldValue(
                      "classListing",
                      selectedOption ? selectedOption.value : null,
                    )
                  }
                  placeholder="Select Class"
                  isClearable
                />

                {formik.touched.classListing && formik.errors.classListing && (
                  <div className="invalid-feedback">
                    {formik.errors.classListing}
                  </div>
                )}
              </div>
              {userName !== "SMS_TEACHER" && (
                <>
                  <div className="col-md-6 col-12 mb-4">
                    <label className="">
                      Teacher<span className="text-danger">*</span>
                    </label>
                    <Select
                      options={teacherOptions}
                      name="userId"
                      value={
                        teacherOptions.find(
                          (option) => option.value === formik.values.userId,
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        formik.setFieldValue(
                          "userId",
                          selectedOption ? selectedOption.value : "",
                        )
                      }
                      placeholder="Select Teacher"
                      isSearchable
                      isClearable
                      className={`${
                        formik.touched.userId && formik.errors.userId
                          ? "is-invalid"
                          : ""
                      }`}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.userId && formik.errors.userId && (
                      <div className="invalid-feedback">
                        {formik.errors.userId}
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-md-6 col-12 mb-4">
                <label className="">
                  Days<span className="text-danger">*</span>
                </label>
                <Select
                  options={[
                    { value: "MONDAY", label: "Monday" },
                    { value: "TUESDAY", label: "Tuesday" },
                    { value: "WEDNESDAY", label: "Wednesday" },
                    { value: "THURSDAY", label: "Thursday" },
                    { value: "FRIDAY", label: "Friday" },
                    { value: "SATURDAY", label: "Saturday" },
                    { value: "SUNDAY", label: "Sunday" },
                  ]}
                  name="day"
                  value={
                    formik.values.day
                      ? {
                          value: formik.values.day,
                          label:
                            formik.values.day.charAt(0) +
                            formik.values.day.slice(1).toLowerCase(),
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    formik.setFieldValue(
                      "day",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  onBlur={() => formik.setFieldTouched("day", true)}
                  placeholder="Select Day"
                  isSearchable
                  isClearable
                  className={
                    formik.touched.day && formik.errors.day ? "is-invalid" : ""
                  }
                />
                {formik.touched.day && formik.errors.day && (
                  <div className="invalid-feedback">{formik.errors.day}</div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label className="">
                  Batch Time<span className="text-danger">*</span>
                </label>
                <Select
                  options={
                    batchData
                      ? batchData.map((time) => ({
                          value:
                            time.includes("AM") || time.includes("PM")
                              ? convertTo24Hour(time)
                              : time,
                          label: normalizeTime(time),
                        }))
                      : []
                  }
                  name="batchTime"
                  value={
                    batchData
                      ? batchData
                          .map((time) => ({
                            value:
                              time.includes("AM") || time.includes("PM")
                                ? convertTo24Hour(time)
                                : time,
                            label: normalizeTime(time),
                          }))
                          .find(
                            (option) =>
                              option.value === formik.values.batchTime,
                          )
                      : null
                  }
                  onChange={(selectedOption) =>
                    formik.setFieldValue(
                      "batchTime",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  onBlur={() => formik.setFieldTouched("batchTime", true)}
                  placeholder="Select Batch Time"
                  isSearchable
                  isClearable
                  className={
                    formik.touched.batchTime && formik.errors.batchTime
                      ? "is-invalid"
                      : ""
                  }
                />
                {formik.touched.batchTime && formik.errors.batchTime && (
                  <div className="invalid-feedback">
                    {formik.errors.batchTime}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-4">
                <label>
                  Date<span className="text-danger">*</span>
                </label>
                <Select
                  options={startEndDate}
                  name="date"
                  value={startEndDate?.find(
                    (option) => option.value === formik.values.date,
                  )}
                  onChange={(selectedOption) =>
                    formik.setFieldValue(
                      "date",
                      selectedOption ? selectedOption.value : "",
                    )
                  }
                  placeholder="Select Date"
                  isSearchable
                  isClearable
                  className={`${
                    formik.touched.date && formik.errors.date
                      ? "is-invalid"
                      : ""
                  }`}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.date && formik.errors.date && (
                  <div className="invalid-feedback">{formik.errors.date}</div>
                )}
              </div>
              {/* <div className="col-md-6 col-12 mb-4">
                <label>
                  Expired Date<span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  name="expiredDate"
                  value={calculateExpiryDate(formik.values.date)}
                  onChange={(date) => formik.setFieldValue("expiredDate", date)}
                  onBlur={formik.handleBlur}
                  error={formik.errors.expiredDate}
                  touched={formik.touched.expiredDate}
                  maxDate={new Date()}
                />
                {formik.touched.expiredDate && formik.errors.expiredDate && (
                  <div className="invalid-feedback">
                    {formik.errors.expiredDate}
                  </div>
                )}
              </div> */}
              <div className="col-md-6 col-12 mb-4">
                <label>
                  Expired Date<span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  className="form-control"
                  value={formatDisplayDate(
                    calculateExpiryDate(formik.values.date),
                  )}
                  readOnly
                  placeholder="Expired Date"
                />
              </div>

              <div className="col-md-6 col-12 mb-4">
                <label className="form-label">
                  Folder Category<span className="text-danger">*</span>
                </label>
                <div className="d-flex">
                  <div>
                    <input
                      className="form-check-input"
                      type="radio"
                      id="individual"
                      name="folderCategoryListing"
                      value="individual"
                      checked={folderCategory === "individual"}
                      onChange={() => {
                        setFolderCategory("individual");
                        formik.setFieldValue(
                          "folderCategoryListing",
                          "individual",
                        );
                      }}
                    />
                    <label htmlFor="individual" className="mx-2">
                      {" "}
                      Individual
                    </label>
                  </div>
                </div>
                {formik.touched.folderCategoryListing &&
                  formik.errors.folderCategoryListing && (
                    <div className="invalid-feedback">
                      {formik.errors.folderCategoryListing}
                    </div>
                  )}
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label>
                  Student Names<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={studentOptions}
                  menuPlacement="top"
                  value={studentOptions.filter((option) =>
                    formik.values.studentIds.includes(option.value),
                  )}
                  onChange={(selected) => {
                    const selectedIds = selected.map((option) => option.value);
                    formik.setFieldValue("studentIds", selectedIds);
                  }}
                  labelledBy="Select Students"
                  className={`dropdown-up ${
                    formik.touched.studentIds && formik.errors.studentIds
                      ? "is-invalid"
                      : ""
                  }`}
                  onBlur={() => formik.setFieldTouched("studentIds", true)}
                />
                {formik.touched.studentIds && formik.errors.studentIds && (
                  <div className="invalid-feedback">
                    {formik.errors.studentIds}
                  </div>
                )}
              </div>
              {formik.values.studentIds.length === 1 && (
                <div className="col-md-6 col-12 mb-4">
                  <label>Folder Name</label>
                  <input
                    name="folderName"
                    type="text"
                    className={`form-control ${
                      formik.touched.folderName && formik.errors.folderName
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("folderName")}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default DocumentAdd;
